import { Base, Event, ResourceType } from "../Common/Base";
import * as React from "react";
import { Header, Segment } from "semantic-ui-react";
import { Layout, Layouts, Responsive, WidthProvider } from "react-grid-layout";
import WidgetRequest from "api/lib/data/interfaces/WidgetRequest";
import WidgetResult from "api/lib/data/interfaces/WidgetResult";
import "react-grid-layout/css/styles.css";
import WidgetLayout from "./WidgetLayout";
import Widget from "api/lib/data/Widget";
import {
  BaseLayout,
  IBaseLayoutMatchParams,
  IBaseLayoutProps,
  IBaseLayoutState,
} from "../Common/BaseLayout";
import Dashboards from "api/lib/data/Dashboards";
import DeleteResource from "../Common/DeleteResource";
import * as routes from "../../constants/routes";
import { UpdateResource } from "../Common/UpdateResource";
import { IRoles } from "src/reducers/session";
import ListUsers from "../Query/ListUsers";

const ResponsiveGridLayout = WidthProvider(Responsive);
interface IMatchParams extends IBaseLayoutMatchParams {
  dashboardId: string;
}

export interface IWidget {
  id: string;
  title: string;
  position: any;
  request: WidgetRequest;
  result: WidgetResult;
}

export interface IDashboardLayoutProps extends IBaseLayoutProps<IMatchParams> {
  orgId: string;
  authUser: any;
}

export interface IDashboardLayoutState extends IBaseLayoutState {
  dashboard: Dashboards;
  dashboardInfo: any;
  layouts: Layouts;
  breakPoint: string;
  canEdit: boolean;
}

export default class DashboardLayout extends BaseLayout<
  IDashboardLayoutProps,
  IDashboardLayoutState
> {
  private static INITIAL_STATE = {
    ...BaseLayout.BASE_LAYOUT_INITAL_STATE,
    dashboard: null,
    dashboardInfo: null,
    layouts: null,
    breakPoint: "md",
    canEdit: false,
  };
  constructor(props: IDashboardLayoutProps) {
    super(props);

    this.state = { ...DashboardLayout.INITIAL_STATE };
  }

  public render() {
    const {
      dashboardInfo,
      layouts,
      breakPoint,
      dashboard,
      canEdit,
    } = this.state;
    return (
      <React.Fragment>
        <Segment className="page-title">
          <Header as="h2">{dashboardInfo && dashboardInfo.name}</Header>
          {dashboardInfo && canEdit && (
            <div className="edit-delete-tool-bar">
              <ListUsers
                {...this.props}
                resource={{
                  id: this.props.match.params.dashboardId,
                  name: dashboardInfo.name,
                  api: dashboard,
                  type: Base.RESOURCE_LABEL[ResourceType.dashboard],
                }}
              />
              <DeleteResource
                {...this.props}
                resource={{
                  id: this.props.match.params.dashboardId,
                  name: dashboardInfo.name,
                  api: dashboard,
                  type: Base.RESOURCE_LABEL[ResourceType.dashboard],
                }}
                deleted={this.handleDashboardDeleted}
              />

              <UpdateResource
                {...this.props}
                resource={{
                  id: this.props.match.params.dashboardId,
                  name: dashboardInfo.name,
                  api: dashboard,
                  type: Base.RESOURCE_LABEL[ResourceType.dashboard],
                }}
                config={dashboardInfo}
                updated={this.handleDashboardUpdated}
              />
            </div>
          )}
        </Segment>

        <Segment className="analysis-layout-tabs">
          {layouts && (
            <ResponsiveGridLayout
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              layouts={this.state.layouts}
              onLayoutChange={this.handleLayoutChanged}
              onBreakpointChange={this.handleBreakPointChanged}
              onResizeStop={this.handleResize}
              className="layout"
              rowHeight={30}
            >
              {layouts &&
                layouts[breakPoint].map((item: Layout) => {
                  return (
                    <div key={item.i} data-grid={item}>
                      <WidgetLayout
                        {...this.props}
                        w={item.w}
                        h={item.h}
                        widget={
                          new Widget(
                            this.props.orgId,
                            this.props.match.params.dashboardId,
                            item.i
                          )
                        }
                      />
                    </div>
                  );
                })}
            </ResponsiveGridLayout>
          )}
        </Segment>
      </React.Fragment>
    );
  }

  public componentDidMount = () => {
    this.getData(this.props);
  };

  public componentDidUpdate = (prevProps, prevState) => {
    if (
      this.props.match.params.dashboardId !== prevProps.match.params.dashboardId
    ) {
      this.getData(this.props);
    }
  };

  private checkRoleAndGetData = (props: IDashboardLayoutProps) => {
    const { orgId, match } = props;
    const dashboardId = match.params.dashboardId;
    const dashboard = new Dashboards(orgId);
    this.setState({ dashboard });
    this.getAuthRolesForResource(dashboard, dashboardId, (roles: IRoles) => {
      if (roles) {
        if (roles && (roles.admin || roles.owner)) {
          this.setState({ canEdit: true });
        } else {
          this.setState({ canEdit: false });
        }
      }
    });
  };

  private getData = (props: IDashboardLayoutProps) => {
    const { orgId, match } = props;
    const dashboardId = match.params.dashboardId;
    this.checkRoleAndGetData(props);
    const dashboard = new Dashboards(orgId);
    this.setState({ dashboard });
    this.getListner(
      dashboard.getConfig(dashboardId),
      Event.Value,
      (snapshot: firebase.database.DataSnapshot) => {
        const dashboardInfo = snapshot.val();
        if (dashboardInfo) {
          this.setState({ dashboardInfo });
          this.getListner(
            dashboard.getLayout(dashboardId),
            Event.Value,
            (layoutSnapshot) => {
              this.setState({
                layouts: JSON.parse((layoutSnapshot.val() || {}).val || null),
              });
            }
          );
        }
      }
    );
  };

  private handleDashboardDeleted = () => {
    const { history, orgId } = this.props;
    history.push(routes.DASHBOARDS(orgId));
  };

  private handleDashboardUpdated = (newName: string) => {
    this.setState({ dashboardInfo: { name: newName } });
  };
  private handleLayoutChanged = (layout, layouts) => {
    const { orgId, match } = this.props;
    const { dashboardId } = match.params;
    const dashboard = new Dashboards(orgId);
    console.log(layout, layouts);
    dashboard.updateLayout(dashboardId, { val: JSON.stringify(layouts) });
  };

  private handleBreakPointChanged = (newBreakPoint: string) => {
    this.setState({ breakPoint: newBreakPoint });
  };

  private handleResize = (
    layout: Layout[],
    oldItem: Layout,
    newItem: Layout,
    placeholder: Layout,
    event: MouseEvent,
    element: HTMLElement
  ) => {
    console.log(layout, oldItem, newItem, placeholder);
    const { orgId, match } = this.props;
    const { dashboardId } = match.params;
    const dashboard = new Dashboards(orgId);
    const { layouts, breakPoint } = this.state;
    layouts[breakPoint] = layout;
    dashboard.updateLayout(dashboardId, { val: JSON.stringify(layouts) });
  };
}



// WEBPACK FOOTER //
// ./src/components/Dashboard/DashboardLayout.tsx